// External Dependencies
import { Link } from 'react-router-dom';
import { UserAlertFlags } from '@presto-assistant/api_types';
import { ExtendedMember } from '@presto-assistant/api_types/api/admin/members';
import { toTitleCase } from '@presto-assistant/api_types/utils/toTitleCase';
import AccountIcon from 'mdi-material-ui/Account';
import AccountTieIcon from 'mdi-material-ui/AccountTie';
import AdminIcon from 'mdi-material-ui/ShieldAccount';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Internal Dependencies
import { EnhancedCardContent } from 'components/shared';
import { getFullName } from 'utils';

// Local Dependencies
import MemberSearchChip from '../../shared/MemberSearchChip';

// Local Typings
interface MemberWithStateLabel extends Partial<ExtendedMember> {
  stateLabel?: string;
}

interface Props {
  member?: MemberWithStateLabel;
}

// Component Definition
const MemberCardHeader = ({ member }: Props): JSX.Element | null => {
  const isFraudUser = member?.alertFlagId === UserAlertFlags.Fraud;
  const hasDobViolation = member?.alertFlagId === UserAlertFlags.DOBViolation;

  const isDistrictMember = Boolean(member?.districtId);

  const isDirector = member?.memberships?.at(-1)?.admin;

  return (
    <EnhancedCardContent className="member-search-result-card-header">
      <Box
        display="flex"
        justifyContent="space-between"
      >
        <Box
          alignItems="center"
          display="flex"
          flexWrap="wrap"
          maxWidth="50%"
        >
          {isDistrictMember && <AdminIcon sx={{ marginRight: 2 }} />}
          {isDirector && <AccountTieIcon sx={{ marginRight: 2 }} />}
          {!isDistrictMember && !isDirector && <AccountIcon sx={{ marginRight: 2 }} />}

          <Typography
            className="member-search-result-header"
            component="h3"
            variant="h6"
          >
            {member?.organizationLabel || member?.districtLabel}
          </Typography>

          <Link
            className="link"
            to={isDistrictMember
              ? `/admin_portals/${member?.districtId}`
              : `/organizations/${member?.organizationId}`}
          >
            link
          </Link>
        </Box>

        <Box
          alignItems="flex-end"
          display="flex"
          gap={1.5}
        >
          {isFraudUser && (
            <MemberSearchChip
              color="error"
              label="Fraud"
              variant="outlined"
            />
          )}
          {hasDobViolation && (
            <MemberSearchChip
              color="error"
              fontWeight={500}
              label="Under 13 years old"
              variant="outlined"
            />
          )}
          <Typography
            className="member-search-result-header"
            component="h4"
            variant="h6"
          >
            {toTitleCase(getFullName({
              firstName: member?.firstName || '',
              lastName: member?.lastName || '',
            }))}
          </Typography>
        </Box>
      </Box>
    </EnhancedCardContent>
  );
};

export default MemberCardHeader;
